import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import { AsmAuthStorageService, CsAgentAuthService, TokenTarget } from '@spartacus/asm/root'
import {
  AuthActions,
  AuthService,
  AuthToken,
  OAuthLibWrapperService,
  OCC_CART_ID_CURRENT,
  OCC_USER_ID_ANONYMOUS,
  OCC_USER_ID_CURRENT,
  WindowRef
} from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbAuthStatePersistenceService } from '../../auth/services/eb-auth-state-persistence.service'
import { EbUserIdService } from '../../auth/services/eb-user-id.service'
import { EbMultiCartService } from '../../cart/services/eb-multi-cart.service'
import { EbUserAccountService } from '../../user/services/eb-user-account.service'
import { EbAsmAdapter, EbAsmTokenResponse } from '../adapters/eb-asm.adapter'

/**
 * Auth service for CS agent. Useful to login/logout agent, start emulation
 * or get information about the status of emulation.
 */
@Injectable({
  providedIn: 'root'
})
export class EbCsagentAuthService extends CsAgentAuthService {
  private readonly inactivityTimeoutMs = 10 * 60 * 1000
  private readonly inactivityTimeLeftSubject = new BehaviorSubject(this.inactivityTimeoutMs / 1000)
  private readonly activityEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const
  private inactivityTimer?: ReturnType<typeof setInterval>
  private inactivityDeadline = 0
  private logoutInProgress?: Promise<void>
  private customerLogoutInProgress?: Promise<boolean>
  private activityListenersAttached = false
  private readonly resetInactivityTimer = () => this.resetTimer()

  constructor(
    protected authService: AuthService,
    protected authStorageService: AsmAuthStorageService,
    protected userIdService: EbUserIdService,
    protected oAuthLibWrapperService: OAuthLibWrapperService,
    protected store: Store,
    protected asmAdapter: EbAsmAdapter,
    protected userAccountService: EbUserAccountService,
    protected cookieService: CookieService,
    protected authStatePersistenceService: EbAuthStatePersistenceService,
    protected winRef: WindowRef,
    protected multiCartService: EbMultiCartService
  ) {
    super(
      authService,
      authStorageService,
      userIdService,
      oAuthLibWrapperService,
      store,
      userAccountService
    )
    if (this.hasActiveCustomerEmulationSession()) {
      this.startInactivityTimer()
    }
  }

  public startCustomerEmulationSession(customerId: string): void {
    this.persistCurrentAgentToken()
    this.authStorageService.clearEmulatedUserToken()
    this.store.dispatch(new AuthActions.Logout())
    this.asmAdapter
      .loadAsmClientSession(customerId)
      .pipe(
        take(1),
        map(() => {
          this.authStatePersistenceService.syncAccessTokenFromCookie()
          this.store.dispatch(new AuthActions.Login())
          this.userIdService.setUserEmulatedState(true)
          this.userIdService.setUserId(OCC_USER_ID_CURRENT)
          this.startInactivityTimer()
          this.multiCartService.loadCart({
            cartId: OCC_CART_ID_CURRENT,
            userId: OCC_USER_ID_CURRENT,
            extraData: {
              active: true
            }
          })
        })
      )
      .subscribe()
  }

  public restoreCustomerEmulationSession() {
    if (this.winRef.localStorage.getItem('emulatedUserToken')) {
      this.authStorageService.setEmulatedUserToken({
        access_token: this.winRef.localStorage.getItem('emulatedUserToken'),
        access_token_stored_at: new Date().toDateString()
      })
    }
    this.store.dispatch(new AuthActions.Login())
    this.userIdService.setUserEmulatedState(true)
    this.startInactivityTimer()
  }

  async authorizeCustomerSupportAgent(userId: string, _password: string): Promise<void> {
    const accessToken = this.accessToken

    this.authStorageService.switchTokenTargetToCSAgent()

    try {
      const asmTokenResponse = await firstValueFrom(this.setAsmAgent(userId))
      this.persistAsmToken(asmTokenResponse)
      this.authStorageService.switchTokenTargetToCSAgent()

      if (accessToken) {
        this.authStorageService.setEmulatedUserToken({
          access_token: accessToken,
          access_token_stored_at: new Date().toDateString()
        })
        this.persistEmulatedUserTokenInStorage(accessToken)
        this.store.dispatch(new AuthActions.Login())
        this.userIdService.setUserEmulatedState(true)
      } else {
        // When we can't get the customerId just end all current sessions
        this.userIdService.setUserId(OCC_USER_ID_ANONYMOUS)
        this.authStorageService.clearEmulatedUserToken()
      }
    } catch (error) {
      this.authStorageService.switchTokenTargetToUser()
      this.clearAsmToken()
      throw error
    }
  }

  isCustomerSupportAgentLoggedIn(): Observable<boolean> {
    return combineLatest([
      this.authStorageService.getToken(),
      this.authStorageService.getTokenTarget(),
      this.userIdService.isEmulated()
    ]).pipe(
      map(([token, tokenTarget, isEmulated]) => {
        const activeAgentToken =
          tokenTarget === TokenTarget.CSAgent && Boolean(token?.access_token?.trim())

        return Boolean(this.cookieService.get('asAgent') && (activeAgentToken || isEmulated))
      })
    )
  }

  getInactivityTimeLeft(): Observable<number> {
    return this.inactivityTimeLeftSubject.asObservable()
  }

  logoutCustomerSupportAgent(): Promise<void> {
    if (!this.logoutInProgress) {
      this.stopInactivityTimer()
      this.logoutInProgress = this.completeLogout()
    }
    return this.logoutInProgress
  }

  stopCustomerImpersonation(): Promise<boolean> {
    if (!this.customerLogoutInProgress) {
      this.customerLogoutInProgress = this.completeCustomerLogout().finally(() => {
        this.customerLogoutInProgress = undefined
      })
    }
    return this.customerLogoutInProgress
  }

  setAsmAgent(agentId: string): Observable<EbAsmTokenResponse> {
    return this.asmAdapter.setAgentSession(agentId).pipe(take(1))
  }

  logoutAsmAgent(): Observable<any> {
    return this.asmAdapter.logoutAsmAgent().pipe(take(1))
  }

  logoutAsmCustomer(): Observable<any> {
    return this.asmAdapter.logoutAsmCustomer()
  }

  /** Restores the CS agent credential after the impersonated customer logs out. */
  restoreCustomerSupportAgentToken(): boolean {
    const agentToken = this.getStoredAsmToken()

    if (!agentToken?.access_token?.trim()) {
      return false
    }

    this.authStorageService.setToken(agentToken)
    this.authStorageService.switchTokenTargetToCSAgent()
    return true
  }

  private async completeLogout(): Promise<void> {
    try {
      await firstValueFrom(this.logoutAsmAgent())
    } catch {
      // An expired backend session must not prevent deterministic local cleanup.
    } finally {
      this.clearCompleteSessionState()
    }
  }

  private async completeCustomerLogout(): Promise<boolean> {
    try {
      await firstValueFrom(this.logoutAsmCustomer().pipe(take(1)))
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return false
      }
    }

    this.authStorageService.clearEmulatedUserToken()
    this.authStatePersistenceService.asmCustomerLogout()
    this.store.dispatch(new AuthActions.Logout())
    this.restoreCustomerSupportAgentToken()
    this.stopInactivityTimer()
    return true
  }

  private clearCompleteSessionState(): void {
    this.stopInactivityTimer()
    this.userIdService.setUserEmulatedState(false)
    this.authStorageService.setTokenTarget(TokenTarget.User)
    this.authStorageService.clearEmulatedUserToken()
    this.clearAsmToken()
    this.winRef.localStorage.removeItem('emulatedUserToken')
    this.winRef.localStorage.removeItem('asm_enabled')
    this.deleteAuthCookie('asAgent')
    this.deleteAuthCookie('temp_customer_emulation_token')
    this.authStatePersistenceService.internalLogout(true, '/')
    this.userAccountService.setEbActiveUserData(undefined)
    this.store.dispatch({ type: '[Auth] Logout Customer Support Agent' })
    this.store.dispatch(new AuthActions.Logout())
    this.reloadAuthenticationState()
  }

  private startInactivityTimer(): void {
    const nativeWindow = this.winRef.nativeWindow
    if (!nativeWindow) {
      return
    }
    if (!this.activityListenersAttached) {
      this.activityEvents.forEach(event =>
        nativeWindow.addEventListener(event, this.resetInactivityTimer, { passive: true })
      )
      this.activityListenersAttached = true
    }
    this.resetTimer()
    if (!this.inactivityTimer) {
      this.inactivityTimer = setInterval(() => this.updateInactivityTimeLeft(), 1000)
    }
  }

  private resetTimer(): void {
    this.inactivityDeadline = Date.now() + this.inactivityTimeoutMs
    this.inactivityTimeLeftSubject.next(this.inactivityTimeoutMs / 1000)
  }

  private updateInactivityTimeLeft(): void {
    const timeLeft = Math.max(0, Math.ceil((this.inactivityDeadline - Date.now()) / 1000))
    this.inactivityTimeLeftSubject.next(timeLeft)
    if (timeLeft === 0) {
      void this.logoutCustomerSupportAgent()
    }
  }

  private stopInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer)
      this.inactivityTimer = undefined
    }
    if (this.activityListenersAttached && this.winRef.nativeWindow) {
      this.activityEvents.forEach(event =>
        this.winRef.nativeWindow.removeEventListener(event, this.resetInactivityTimer)
      )
      this.activityListenersAttached = false
    }
  }

  private hasActiveCustomerEmulationSession(): boolean {
    return Boolean(
      this.cookieService.get('asAgent') &&
      (this.winRef.localStorage?.getItem('emulatedUserToken') ||
        this.cookieService.get('temp_customer_emulation_token'))
    )
  }

  private deleteAuthCookie(name: string): void {
    const hostname = this.winRef.location.hostname
    this.cookieService.delete(name, '/', hostname)
    if (hostname && (hostname.match(/\./g) || []).length > 1) {
      this.cookieService.delete(name, '/', hostname.substring(hostname.indexOf('.')))
    }
  }

  private reloadAuthenticationState(): void {
    this.winRef.nativeWindow?.location.assign('/')
  }

  private persistEmulatedUserTokenInStorage(accessToken: string) {
    this.winRef.localStorage.setItem('emulatedUserToken', accessToken)
  }

  private persistAsmToken(tokenResponse: EbAsmTokenResponse): void {
    const accessToken = this.getAsmAccessToken(tokenResponse)

    if (!accessToken) {
      this.clearAsmToken()
      throw new Error('ASM login did not return an access token.')
    }

    const authToken = this.createAsmAuthToken(accessToken, tokenResponse.expires_in)

    this.authStorageService.setToken(authToken)
    this.winRef.localStorage.setItem(EbLocalStorageKey.ASM_KEY, JSON.stringify(authToken))
  }

  private clearAsmToken(): void {
    this.authStorageService.setToken({} as AuthToken)
    this.winRef.localStorage.removeItem(EbLocalStorageKey.ASM_KEY)
  }

  private persistCurrentAgentToken(): void {
    this.authStorageService
      .getToken()
      .pipe(take(1))
      .subscribe(token => {
        if (token?.access_token?.trim()) {
          this.winRef.localStorage.setItem(EbLocalStorageKey.ASM_KEY, JSON.stringify(token))
        }
      })
  }

  private getStoredAsmToken(): AuthToken | undefined {
    try {
      const token = JSON.parse(
        this.winRef.localStorage?.getItem(EbLocalStorageKey.ASM_KEY)
      ) as AuthToken

      return token
    } catch {
      return undefined
    }
  }

  private getAsmAccessToken(tokenResponse?: EbAsmTokenResponse): string | undefined {
    return (
      tokenResponse?.access_token?.trim() ||
      tokenResponse?.accessToken?.trim() ||
      tokenResponse?.token?.access_token?.trim() ||
      tokenResponse?.token?.accessToken?.trim()
    )
  }

  private createAsmAuthToken(accessToken: string, expiresIn?: number): AuthToken {
    const storedAt = new Date().getTime()

    return {
      access_token: accessToken,
      token_type: 'Bearer',
      expires_at: expiresIn ? (storedAt + expiresIn * 1000).toString() : undefined,
      granted_scopes: ['basic'],
      access_token_stored_at: storedAt.toString()
    }
  }

  get accessToken() {
    return (
      this.cookieService.get('access_token') ||
      this.cookieService.get('temp_customer_emulation_token')
    )
  }
}
